/**
 * ComposeDialog — honest reply/compose modal for the Eingang reading pane (Story UI-U9).
 *
 * Ported from the v0 MailComposeDialog, hand-rolled to OUR modal pattern
 * (OutboundDetailDialog). HONEST: there is no broker send endpoint yet, so the only
 * action is «Als Entwurf speichern» (local, per-mail) with a «Versand folgt» hint.
 * It does NOT fake a send and does NOT flip triage.
 *
 * Story MC6 layers (additively — the «Senden» button + real send are the sibling
 * send story's, NOT added here):
 *   - `mode: 'compose'` allows a mail-less "Neue Nachricht" open (empty fields).
 *   - `sendingMailbox` surfaces "Von: <email>" (which connected mailbox WOULD send).
 *   - `onConnectRequest` turns the no-connection hint into a real "Postfach verbinden"
 *     affordance (opens the shared ProviderChoiceModal) instead of dead-ending.
 * When neither prop is passed (the UI-U9 reply path) the dialog is unchanged.
 *
 * [Source: brokly-design-reference components/brokly/mail-dialogs.tsx#MailComposeDialog]
 * [Source: apps/web/src/features/communication-v2/components/OutboundDetailDialog.tsx] (modal pattern)
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §5 Overlays]
 */
import { useEffect, useState } from 'react';
import { Save, Send, Sparkles, Wand2, Loader2, X, Mailbox, PenLine } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { refreshCommAssist } from '../api/comm-assist-api';
import { apiClient } from '../../../lib/api-client';
import type { EingangMail } from '../types';
import type { MailboxProvider } from '../../mailbox/types';

export interface ComposeDraft {
  to: string;
  betreff: string;
  text: string;
  /**
   * The HTML body a real send should use (B3 rich). Present only on the send path (not on the
   * local draft-save path). Carries the plain message as HTML plus, when a signature was
   * inserted, the sanitised RICH signature HTML instead of its plain-text stand-in.
   */
  bodyHtml?: string;
}

export type ComposeMode = 'reply' | 'compose';

/** The connected mailbox a send WOULD leave from (MC6 surfacing). */
export interface SendingMailbox {
  email: string;
  provider: MailboxProvider;
}

interface ComposeDialogProps {
  mail: EingangMail | null;
  open: boolean;
  /** Seed values (a previously saved local draft, or the KI-Vorschlag prefill). */
  initialDraft: ComposeDraft;
  onOpenChange: (open: boolean) => void;
  onSaveDraft: (draft: ComposeDraft) => void;
  /** 'reply' (default, needs a mail) | 'compose' (new message, mail-less). MC6. */
  mode?: ComposeMode;
  /** The connected mailbox that would send — surfaces "Von: <email>". MC6. */
  sendingMailbox?: SendingMailbox | null;
  /** No connected mailbox → open the provider-choice modal from the hint. MC6. */
  onConnectRequest?: () => void;
  /**
   * The communication this reply is on. When set, the "KI-Vorschlag generieren"
   * action force-regenerates the draft (POST …/assist/refresh) into the editor.
   */
  commId?: string | null;
  /**
   * Real send handler (POST /mailbox/send under the hood). When provided a primary
   * "Senden" button appears (and the "Versand folgt" draft-only hint is suppressed) —
   * the parent threads the reply onto `commId` and moves it to Gesendet on success.
   */
  onSend?: (draft: ComposeDraft) => Promise<void>;
}

export function ComposeDialog({
  mail,
  open,
  initialDraft,
  onOpenChange,
  onSaveDraft,
  mode = 'reply',
  sendingMailbox = null,
  onConnectRequest,
  commId = null,
  onSend,
}: ComposeDialogProps) {
  const [to, setTo] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  // KI-Vorschlag generieren (force-regenerate) state.
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  // Senden (real send) state.
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // B3-1: the caller's own signature, fetched once per open and inserted on demand.
  // `signature` is the PLAIN form (shown in the textarea); `signatureHtml` is the sanitised
  // RICH form injected into the HTML body on send (B3 rich).
  const [signature, setSignature] = useState<string | null>(null);
  const [signatureHtml, setSignatureHtml] = useState<string | null>(null);
  const [signatureInserted, setSignatureInserted] = useState(false);

  // Seed the fields when the modal opens (or the selected mail changes). The
  // seed is intentionally keyed on open + mail id (not initialDraft, which is
  // re-derived every render in the pane) so typing is not clobbered mid-edit.
  const mailId = mail?.id;
  useEffect(() => {
    if (open) {
      setTo(initialDraft.to);
      setBetreff(initialDraft.betreff);
      setText(initialDraft.text);
      setGenError(null);
      setSendError(null);
      setSignatureInserted(false);
    }
    // Effect is keyed on open + mailId only (see comment); initialDraft is stable per open.
  }, [open, mailId]);

  // B3-1: load the caller's signature when the dialog opens.
  //
  // The button only appears once a signature exists. Showing a disabled "Signatur einfügen"
  // to somebody who has never defined one would be a dead control that teaches nothing; the
  // Administration panel is where a signature gets created, and this is where it gets used.
  // A failed load is silent on purpose: a missing signature must not block writing the mail.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiClient.emailSignature
      .get()
      .then((res) => {
        if (!cancelled) {
          setSignature(res.signature?.rendered?.trim() || null);
          setSignatureHtml(res.signature?.renderedHtml?.trim() || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSignature(null);
          setSignatureHtml(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  /**
   * Append the signature to the body.
   *
   * Appends rather than replaces, and appends the api's `rendered` string verbatim: the
   * org-wide template lives on the server precisely so two employees' signatures cannot
   * drift, and re-assembling the parts here would reintroduce that drift. Guarded against a
   * double insert, because a signature pasted twice is the classic version of this bug.
   */
  function handleInsertSignature() {
    if (signature === null || signatureInserted) return;
    setText((prev) =>
      prev.trimEnd().length > 0 ? `${prev.trimEnd()}\n\n${signature}` : signature,
    );
    setSignatureInserted(true);
  }

  /**
   * Build the HTML body a real send should carry (B3 rich).
   *
   * The email body is HTML, so the message text is escaped and its newlines become `<br>`.
   * When a signature was inserted, the textarea currently ends with the PLAIN signature — we
   * strip that stand-in and inject the sanitised RICH signature HTML instead, so the sent
   * mail carries the formatted signature (bold, links, logo) rather than its flattened text.
   * If the broker edited the signature text by hand, the suffix no longer matches and we fall
   * back to escaping the whole thing — correct, just without the rich formatting.
   */
  function buildBodyHtml(): string {
    const esc = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const nl2br = (s: string) => esc(s).replace(/\r?\n/g, '<br>');

    if (signatureInserted && signatureHtml && signature) {
      const trimmed = text.replace(/\s+$/, '');
      if (trimmed.endsWith(signature)) {
        const message = trimmed.slice(0, trimmed.length - signature.length).replace(/\s+$/, '');
        return message ? `${nl2br(message)}<br><br>${signatureHtml}` : signatureHtml;
      }
    }
    return nl2br(text);
  }

  // Close on Esc (OutboundDetailDialog precedent).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  /** Force-regenerate the KI draft into the editor (POST …/assist/refresh). */
  async function handleGenerate() {
    if (!commId || generating) return;
    setGenerating(true);
    setGenError(null);
    try {
      const result = await refreshCommAssist(commId);
      if (result?.draft) {
        setText(result.draft);
      } else {
        setGenError('Kein Vorschlag verfügbar.');
      }
    } catch {
      setGenError('Vorschlag konnte nicht erzeugt werden.');
    } finally {
      setGenerating(false);
    }
  }

  /** Real send via the injected handler (parent closes + moves to Gesendet on success). */
  async function handleSend() {
    if (!onSend || sending) return;
    const trimmed = to.trim();
    if (!trimmed) {
      setSendError('Empfänger fehlt.');
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      await onSend({ to: trimmed, betreff: betreff.trim(), text, bodyHtml: buildBodyHtml() });
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Senden fehlgeschlagen.');
    } finally {
      setSending(false);
    }
  }

  // 'reply' needs a selected mail; 'compose' (MC6 "Verfassen") opens mail-less.
  if (!open) return null;
  if (mode === 'reply' && !mail) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-xl border bg-card shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b p-4">
          <div className="flex flex-col gap-0.5 pr-4">
            <h2 className="font-semibold text-foreground">
              {mode === 'compose' ? 'Neue Nachricht' : 'Antworten'}
            </h2>
            <p className="text-xs text-muted-foreground">
              {mode === 'compose'
                ? 'Neue E-Mail verfassen: Empfänger und Betreff eingeben.'
                : onSend
                  ? 'KI-Vorschlag prüfen, anpassen und senden.'
                  : 'Von der KI vorgeschlagen: vor dem Speichern prüfen und anpassen.'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Schliessen"
            className="size-7 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Body */}
        <form
          id="compose-form"
          className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSaveDraft({ to: to.trim(), betreff: betreff.trim(), text });
          }}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="compose-to" className="text-xs text-muted-foreground">
              An
            </label>
            <input
              id="compose-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="empfaenger@beispiel.ch"
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="compose-betreff" className="text-xs text-muted-foreground">
              Betreff
            </label>
            <input
              id="compose-betreff"
              type="text"
              value={betreff}
              onChange={(e) => setBetreff(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="compose-text"
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <Sparkles className="size-3.5 text-sem-info" />
                Nachricht (KI-Vorschlag)
              </label>
              {/* KI-Vorschlag generieren — force-regenerate the draft into the editor. */}
              {commId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="h-7 text-xs text-primary"
                >
                  {generating ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="size-3.5" />
                  )}
                  KI-Vorschlag generieren
                </Button>
              )}
              {/* B3-1: the half of B3 a broker actually touches. Only rendered once a
                  signature exists, so it is never a dead control. */}
              {signature !== null && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleInsertSignature}
                  disabled={signatureInserted}
                  className="h-7 text-xs"
                >
                  <PenLine className="size-3.5" />
                  {signatureInserted ? 'Signatur eingefügt' : 'Signatur einfügen'}
                </Button>
              )}
            </div>
            {genError && (
              <p className="text-xs text-destructive" role="alert">
                {genError}
              </p>
            )}
            <textarea
              id="compose-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="min-h-44 resize-none whitespace-pre-line rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t p-4">
          <div className="flex min-w-0 flex-col gap-0.5">
            {/* MC6 sending-mailbox surfacing / connect hint (additive; absent on the
                UI-U9 reply path where neither prop is passed). */}
            {sendingMailbox != null ? (
              <span className="text-xs text-muted-foreground">
                Von: <span className="font-mono text-foreground">{sendingMailbox.email}</span>
              </span>
            ) : onConnectRequest != null ? (
              <button
                type="button"
                onClick={onConnectRequest}
                className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-primary underline-offset-4 hover:underline"
              >
                <Mailbox className="size-3.5" />
                Postfach verbinden, um zu senden
              </button>
            ) : null}
            {/* Honest hint ONLY when there is no real send path (draft-only). */}
            {onSend == null && (
              <span className="text-xs text-muted-foreground">
                Versand folgt, der Entwurf wird lokal gehalten.
              </span>
            )}
            {sendError && (
              <span className="text-xs text-destructive" role="alert">
                {sendError}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="compose-form" variant={onSend ? 'outline' : 'default'}>
              <Save className="size-4" />
              Als Entwurf speichern
            </Button>
            {onSend && (
              <Button type="button" onClick={handleSend} disabled={sending || !to.trim()}>
                {sending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                Senden
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
